import { useState } from 'react'
import { ApiError } from '../api/client'
import {
  useAlertKinds,
  useAlertRecipients,
  useDeleteAlertRecipient,
  useSaveAlertRecipient,
} from '../api/hooks'
import { useAuth } from '../auth/AuthContext'
import type { AlertRecipient } from '../api/types'

/**
 * Who this organization tells when a close fails.
 *
 * Recipients are per organization, which is the reason this screen exists at all — the
 * previous arrangement was one static list in Azure Monitor that knew nothing about whose
 * run failed, so every organization's operations team would have read every other
 * organization's failures.
 */
export function AlertRecipientsPanel() {
  const { hasRole } = useAuth()
  const isAdmin = hasRole('Admin')

  const { data: recipients = [], isLoading } = useAlertRecipients(isAdmin)
  const { data: kinds = [] } = useAlertKinds(isAdmin)

  const save = useSaveAlertRecipient()
  const remove = useDeleteAlertRecipient()

  const [editing, setEditing] = useState<number | 'new' | null>(null)
  const [email, setEmail] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [chosen, setChosen] = useState<string[]>([])
  const [error, setError] = useState<string | null>(null)

  if (!isAdmin) {
    return null
  }

  function open(recipient: AlertRecipient | 'new') {
    setError(null)
    save.reset()
    setEditing(recipient === 'new' ? 'new' : recipient.alertRecipientId)
    setEmail(recipient === 'new' ? '' : recipient.email)
    setDisplayName(recipient === 'new' ? '' : (recipient.displayName ?? ''))
    setChosen(recipient === 'new' ? [] : recipient.alertKinds)
  }

  function submit() {
    setError(null)

    save.mutate(
      {
        alertRecipientId: editing === 'new' ? undefined : (editing as number),
        email: email.trim(),
        displayName: displayName.trim() === '' ? null : displayName.trim(),
        // Empty means all. Sent as null so the API stores "no opinion" rather than an
        // empty subscription, which would mean the opposite.
        alertKinds: chosen.length === 0 ? null : chosen,
        isActive: true,
      },
      {
        onSuccess: () => setEditing(null),
        onError: (e) =>
          setError(e instanceof ApiError ? e.message : 'The recipient could not be saved.'),
      },
    )
  }

  function toggle(name: string) {
    setChosen((current) =>
      current.includes(name) ? current.filter((k) => k !== name) : [...current, name])
  }

  const active = recipients.filter((r) => r.isActive)

  return (
    <section className="border-t" style={{ borderColor: 'var(--color-rule)' }}>
      <div
        className="flex flex-wrap items-center gap-3 px-4 py-2"
        style={{ background: 'var(--color-panel-alt)' }}
      >
        <h2 className="col-head">alert recipients</h2>
        <span className="text-[11px]" style={{ color: 'var(--color-faint)' }}>
          Who this organization tells when a scheduled close fails. Nobody is watching at
          2 a.m., which is the whole reason these exist.
        </span>
        <button className="btn btn-sm ml-auto" onClick={() => open('new')}>
          Add recipient
        </button>
      </div>

      {/*
          Said plainly rather than left to be discovered after an incident. An empty list
          is a legitimate choice -- somebody watches the dashboard -- but it is also
          exactly what a half-finished setup looks like, and the two are indistinguishable
          from this screen.
      */}
      {!isLoading && active.length === 0 && (
        <p
          className="px-4 py-1.5 text-[12px]"
          style={{ background: 'var(--color-warn-wash)', color: 'var(--color-warn)' }}
        >
          No recipients. If a scheduled close fails, the alert is recorded but nobody is
          told.
        </p>
      )}

      <table className="w-full border-collapse">
        <thead>
          <tr className="border-b text-left" style={{ borderColor: 'var(--color-rule)' }}>
            <th className="col-head px-4 py-1.5">Email</th>
            <th className="col-head px-4 py-1.5">Name</th>
            <th className="col-head px-4 py-1.5">Alerts</th>
            <th className="col-head px-4 py-1.5" />
          </tr>
        </thead>
        <tbody>
          {recipients.map((r) => (
            <tr
              key={r.alertRecipientId}
              className="border-b"
              style={{
                borderColor: 'var(--color-rule-soft)',
                opacity: r.isActive ? 1 : 0.55,
              }}
            >
              <td className="ident px-4 py-1.5">
                {r.email}
                {!r.isActive && (
                  <span
                    className="badge ml-1.5"
                    style={{ background: 'var(--color-panel-alt)', color: 'var(--color-muted)' }}
                  >
                    silenced
                  </span>
                )}
              </td>
              <td className="px-4 py-1.5 text-[12px]" style={{ color: 'var(--color-muted)' }}>
                {r.displayName}
              </td>
              <td className="px-4 py-1.5 text-[12px]">
                {r.wantsAll ? (
                  <span style={{ color: 'var(--color-muted)' }}>all</span>
                ) : (
                  r.alertKinds.join(', ')
                )}
              </td>
              <td className="px-4 py-1.5 text-right">
                <button
                  className="text-[12px] underline"
                  style={{ color: 'var(--color-accent)' }}
                  onClick={() => open(r)}
                >
                  Edit
                </button>
                {r.isActive && (
                  <button
                    className="ml-3 text-[12px] underline"
                    style={{ color: 'var(--color-error)' }}
                    onClick={() => remove.mutate(r.alertRecipientId)}
                  >
                    Remove
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {editing !== null && (
        <div
          className="border-t px-4 py-3"
          style={{ background: 'var(--color-panel-alt)', borderColor: 'var(--color-rule-soft)' }}
        >
          <div className="flex flex-wrap items-end gap-3">
            <label className="flex flex-col gap-1 text-[12px]">
              <span className="col-head">email</span>
              <input
                className="input w-72"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="close-alerts@example.com"
              />
            </label>

            <label className="flex flex-col gap-1 text-[12px]">
              <span className="col-head">name (optional)</span>
              <input
                className="input w-56"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="Finance team"
              />
            </label>

            <button className="btn btn-sm" onClick={submit} disabled={save.isPending}>
              {save.isPending ? 'Saving…' : 'Save'}
            </button>
            <button
              className="text-[12px] underline"
              style={{ color: 'var(--color-muted)' }}
              onClick={() => setEditing(null)}
            >
              Cancel
            </button>
          </div>

          <div className="mt-3">
            <span className="col-head">alerts</span>
            <p className="mb-1.5 text-[11px]" style={{ color: 'var(--color-faint)' }}>
              Choose none to receive every alert, including any added in future.
            </p>

            <div className="flex flex-col gap-1">
              {kinds.map((k) => (
                <label key={k.name} className="flex items-start gap-2 text-[12px]">
                  <input
                    type="checkbox"
                    className="mt-0.5"
                    checked={chosen.includes(k.name)}
                    onChange={() => toggle(k.name)}
                  />
                  <span>
                    <span className="ident">{k.name}</span>
                    <span className="ml-2" style={{ color: 'var(--color-faint)' }}>
                      {k.description}
                    </span>
                  </span>
                </label>
              ))}
            </div>
          </div>

          {error && (
            <p className="mt-2 text-[12px]" style={{ color: 'var(--color-error)' }} role="alert">
              {error}
            </p>
          )}
        </div>
      )}
    </section>
  )
}
